/**
 * The buffers of one world buffer's screen-space reflection, as `ssr_allocate_buffers` and
 * `allocate_last_frame_buffer` size them (`ss_effects.cpp:366-394`, `:1432-1471`). Each holds
 * Godot's rows, top row first.
 */
import * as THREE from 'three';
import { ssrMipCount, ssrTraceSize } from '../../godot/rendering/environment/screenSpaceReflection.js';
import { createPassTarget, NEAREST_TARGET } from '../environment/passTarget.js';
import { NORMAL_ROUGHNESS } from '../screenSpaceEffects/prepassBuffers.js';
import { withMipLevels } from '../mipLevels.js';
import { reversedTexelRows } from '../texelCopy.js';
import { FILTER_RADIUS } from './ssrShaders.js';
/** Godot's R16G16B16A16_SFLOAT colour, read through its linear clamped sampler. */
const HALF_FLOAT_LINEAR = {
    ...NEAREST_TARGET,
    type: THREE.HalfFloatType,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
};
/** The roughness filter's blur across, kept at the 32 bits Godot's shared cache holds it in. */
const FLOAT_ACROSS = { ...NEAREST_TARGET, type: THREE.FloatType };
/** Godot's R32_SFLOAT depth pyramid. */
const DEPTH_LEVEL = {
    ...NEAREST_TARGET,
    type: THREE.FloatType,
    format: THREE.RedFormat,
};
export class ReflectionBuffers {
    shape;
    /** The frame's rows in Godot's order, which the trace reads at full size. */
    rowsReversed;
    mipCount;
    /** The depth pyramid, nearest the camera of each block, one level per mip. */
    hiz;
    hizLevels;
    /**
     * The normal and roughness the trace reads, in Godot's rows: at half size, of the sample the depth
     * pyramid's base keeps.
     */
    normalRoughness;
    /** The trace's colour, then the mip each pixel's roughness reads. */
    trace;
    /** The traced colour, blurred more at each mip by the roughness filter. */
    chain;
    chainLevels;
    /** Each mip of the chain from 1 blurred across, with the rows past each edge that the blur down reads. */
    chainAcross;
    /** The reflection at the world buffer's size, as the colour pass reads it. */
    reflection;
    /** The last frame, at the trace's size, cleared to zero before the first frame. */
    lastFrame;
    /**
     * Allocates every target a copy reads or writes. A copy into a target no draw has bound finds none
     * of its levels, and a target first bound between a draw and its copy leaves its own texture on the
     * active unit, which the copy then writes into: a feedback loop.
     */
    constructor(renderer, shape) {
        const { width, height, isHalfSize } = shape;
        this.shape = { ...shape };
        const trace = ssrTraceSize({ width, height }, isHalfSize);
        this.mipCount = ssrMipCount(trace);
        this.rowsReversed = reversedTexelRows(width, height);
        this.hiz = chainTarget(trace, this.mipCount, {
            ...DEPTH_LEVEL,
            minFilter: THREE.NearestMipmapNearestFilter,
        });
        this.hizLevels = levelTargets(trace.width, trace.height, this.mipCount, DEPTH_LEVEL);
        this.normalRoughness = new THREE.WebGLRenderTarget(trace.width, trace.height, NORMAL_ROUGHNESS);
        this.trace = traceTarget(trace.width, trace.height);
        this.chain = chainTarget(trace, this.mipCount, {
            ...HALF_FLOAT_LINEAR,
            minFilter: THREE.LinearMipmapLinearFilter,
        });
        this.chainLevels = levelTargets(trace.width, trace.height, this.mipCount, HALF_FLOAT_LINEAR);
        this.chainAcross = levelTargets(trace.width, trace.height, this.mipCount, FLOAT_ACROSS, 2 * FILTER_RADIUS);
        this.reflection = createPassTarget('SsrReflection', NEAREST_TARGET);
        this.reflection.setSize(width, height);
        this.lastFrame = createPassTarget('SsrLastFrame', HALF_FLOAT_LINEAR);
        this.lastFrame.setSize(trace.width, trace.height);
        for (const target of [this.hiz, this.chain, this.trace, ...this.hizLevels, ...this.chainLevels]) {
            renderer.initRenderTarget(target);
        }
    }
    /** The target mip `level` of the depth pyramid draws into, from 1. */
    hizLevel(level) {
        return this.hizLevels[level - 1];
    }
    /** The target mip `level` of the roughness chain draws into, from 1. */
    chainLevel(level) {
        return this.chainLevels[level - 1];
    }
    /** The target the blur across of mip `level` of the roughness chain draws into, from 1. */
    chainAcrossLevel(level) {
        return this.chainAcross[level - 1];
    }
    dispose() {
        for (const target of this.targets())
            target.dispose();
    }
    targets() {
        const own = [this.hiz, this.normalRoughness, this.trace, this.chain, this.reflection, this.lastFrame];
        return [...own, ...this.hizLevels, ...this.chainLevels, ...this.chainAcross];
    }
}
/** Godot's R8 mip store sits beside the colour, as the trace writes both (`screen_space_reflection.glsl:24-25`). */
function traceTarget(width, height) {
    const target = new THREE.WebGLRenderTarget(width, height, { ...HALF_FLOAT_LINEAR, count: 2 });
    const mipLevel = target.textures[1];
    mipLevel.type = THREE.UnsignedByteType;
    mipLevel.format = THREE.RedFormat;
    mipLevel.minFilter = THREE.NearestFilter;
    mipLevel.magFilter = THREE.NearestFilter;
    return target;
}
/** A chain of `mipCount` mips, `size` at mip 0, which a read reaches across its mips. */
function chainTarget(size, mipCount, options) {
    return withMipLevels(new THREE.WebGLRenderTarget(size.width, size.height, options), mipCount);
}
/** A target for each mip from 1 of a chain `width` by `height`, each `extraRows` taller than its mip. */
function levelTargets(width, height, mipCount, options, extraRows = 0) {
    return Array.from({ length: mipCount - 1 }, (_, index) => {
        const level = index + 1;
        const rows = Math.max(1, height >> level) + extraRows;
        return new THREE.WebGLRenderTarget(Math.max(1, width >> level), rows, options);
    });
}
//# sourceMappingURL=reflectionBuffers.js.map